/**
 * Breadcrumb-chain helpers — issue #122 + #753.
 *
 * The bug: every page registers its own crumb list ad-hoc via
 * `useBreadcrumbs(...)`. Cycle-scoped routes (`/cycles/:cycleId/...`)
 * historically registered `[Home, <cycle>]`, skipping the team
 * segment. The lead's only clickable crumb was `Home`, which jumped
 * past both the cycle dashboard AND the team page in a single hop.
 *
 * Fix shape (Option B): keep the per-page `useBreadcrumbs(...)` feed
 * but introduce two helpers that resolve the leading chain so every
 * call site stays consistent without forcing legal/module pages
 * through a URL-derived pipeline.
 *
 *   - `useTeamCrumbs(teamId)` — returns `[Home, <team>]`. Each call
 *     site appends its own leaf segment.
 *   - `useCycleCrumbs(cycleId)` — returns `[Home, <team>, <cycle>]`,
 *     resolving the team via the cycle's `team_id`. Each call site
 *     appends its own leaf segment.
 *   - `useTaskMapCrumbs(teamId, leafLabel?)` — #753, B7.3. Returns
 *     `[<team name>, Map]` (+ optional leaf) for tasks-team breadcrumbs.
 *     The team name links to the map home (`/teams/:id/map`).
 *     Rooted at the team output, not at "Home" — per B7.3 design.
 *
 * Loading-state shape: while the team query is in flight (or 403s)
 * the helper still emits the team segment with a fallback label so
 * the chain stays stable; the `to` link is correct because we know
 * the team_id from the URL (or from the cycle response). Same
 * pattern `cycleBreadcrumbLabel` already uses for the cycle segment.
 */
import { useCycle } from "../api/useCycle";
import { useTeam } from "../api/useTeam";
import type { Crumb } from "../components/Breadcrumbs";
import { cycleBreadcrumbLabel } from "./cycleBreadcrumb";

const HOME_CRUMB: Crumb = { label: "Home", to: "/" };

/** Fallback label when the team query is still in flight or 403s. */
const TEAM_FALLBACK_LABEL = "Team";

/**
 * `[Home, <team>]` chain. Returns just `[Home]` if `teamId` is
 * undefined (e.g. on the home page). The team segment carries a
 * working `to` even before `useTeam` resolves — only the label
 * falls back.
 */
export function useTeamCrumbs(teamId: string | undefined): Crumb[] {
  const teamQuery = useTeam(teamId);
  if (!teamId) return [HOME_CRUMB];
  return [
    HOME_CRUMB,
    {
      label: teamQuery.data?.name ?? TEAM_FALLBACK_LABEL,
      to: `/teams/${teamId}`,
    },
  ];
}

/**
 * `[<team name> → /teams/:id/map, Map]` chain for tasks-team breadcrumbs.
 *
 * B7.3 — a tasks team's breadcrumb trail is rooted at the team output
 * and walks the node path. For Phase 0 we ship the team root + Map.
 * Deep node trails come with #759 (the real tree editor).
 *
 * Example render: `🗺 Cardio-Imaging Study › Publish the study › Lit review`
 * Phase 0 render: `<team name> › Map`
 *
 * The chain does NOT include a leading "Home" crumb — tasks breadcrumbs
 * are rooted at the team itself (the "output"), not the app root.
 * This matches the B7.3 wireframe `🗺 Cardio-Imaging Study › ...`.
 */
export function useTaskMapCrumbs(
  teamId: string | undefined,
  leafLabel?: string,
): import("../components/Breadcrumbs").Crumb[] {
  const teamQuery = useTeam(teamId);
  if (!teamId) return leafLabel ? [{ label: leafLabel }] : [];
  const mapHome = `/teams/${teamId}/map`;
  const teamCrumb: import("../components/Breadcrumbs").Crumb = {
    label: teamQuery.data?.name ?? TEAM_FALLBACK_LABEL,
    to: mapHome,
  };
  if (!leafLabel) return [teamCrumb];
  return [teamCrumb, { label: leafLabel }];
}

/**
 * `[Home, <team>, <cycle>]` chain. Returns:
 *
 *   - `[Home]` if `cycleId` is undefined.
 *   - `[Home, <cycle>]` while the cycle query is in flight (we don't
 *     yet know the team_id). Once the cycle resolves we get the team
 *     segment too.
 *   - `[Home, <team>, <cycle>]` once we have the team_id.
 *
 * Both intermediate segments carry a working `to`. The team label
 * falls back to "Team" while `useTeam` is in flight; the cycle label
 * falls back to `cycleBreadcrumbLabel`'s id-prefix while `useCycle`
 * is in flight.
 */
export function useCycleCrumbs(cycleId: string | undefined): Crumb[] {
  const cycleQuery = useCycle(cycleId);
  const teamId = cycleQuery.data?.team_id;
  const teamQuery = useTeam(teamId);

  if (!cycleId) return [HOME_CRUMB];

  const cycleCrumb: Crumb = {
    label: cycleBreadcrumbLabel(cycleQuery.data ?? { id: cycleId }),
    to: `/cycles/${cycleId}`,
  };

  if (!teamId) {
    // Cycle hasn't resolved yet (or errored before yielding team_id).
    // Render `[Home, <cycle>]` so the chain stays stable; once the
    // cycle resolves the team segment slots in.
    return [HOME_CRUMB, cycleCrumb];
  }

  return [
    HOME_CRUMB,
    {
      label: teamQuery.data?.name ?? TEAM_FALLBACK_LABEL,
      to: `/teams/${teamId}`,
    },
    cycleCrumb,
  ];
}
